import React, { useState, useEffect } from 'react';
import { CartProvider } from './CartContext';
import { AuthProvider, useAuth } from './AuthContext';
import Navbar from './components/Navbar';
import FloatingCartButton from './components/FloatingCartButton';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderTracking from './pages/OrderTracking';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AdminPanel from './pages/AdminPanel';
import UserProfile from './pages/UserProfile';
import SplashScreen from './components/SplashScreen';
import { Toaster } from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import './App.css';

function AppInner() {
  const { user, loading } = useAuth();
  const [page, setPage]               = useState('login');
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [splash, setSplash]           = useState(true);

  // After Firebase auth check resolves, decide first page
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        setSplash(false);
        if (!user) {
          setPage('login');
        } else {
          setPage('home');
        }
      }, 1800); // show splash 1.8 s
      return () => clearTimeout(timer);
    }
  }, [loading, user]);

  const navigate = (p, data) => {
    if (p === 'track' && data) setTrackingOrder(data);
    // Guard admin-only page
    if (p === 'admin' && user?.role !== 'admin') { setPage('home'); return; }
    // Guard protected pages
    if (['cart','checkout','profile'].includes(p) && !user) { setPage('login'); return; }
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (splash || loading) return <SplashScreen />;

  // If not logged in force login/signup pages only
  if (!user && !['login','signup'].includes(page)) {
    return <LoginPage navigate={(p,d) => { setPage(p); }} />;
  }

  const noNav   = ['checkout','track','login','signup','admin'];
  const noFloat = ['checkout','track','cart','login','signup','admin'];

  return (
    <div className="app">
      {!noNav.includes(page) && <Navbar navigate={navigate} currentPage={page} />}

      {page === 'login'    && <LoginPage    navigate={(p,d) => { if(p==='track'&&d) setTrackingOrder(d); setPage(p); }} />}
      {page === 'signup'   && <SignupPage   navigate={(p,d) => { setPage(p); }} />}
      {page === 'home'     && user && <HomePage     navigate={navigate} />}
      {page === 'products' && user && <ProductsPage navigate={navigate} />}
      {page === 'cart'     && user && <CartPage     navigate={navigate} />}
      {page === 'checkout' && user && <CheckoutPage navigate={navigate} />}
      {page === 'track'    && user && <OrderTracking navigate={navigate} order={trackingOrder} />}
      {page === 'profile'  && user && <UserProfile  navigate={navigate} />}
      {page === 'admin'    && user?.role === 'admin' && <AdminPanel navigate={navigate} />}

      {!noFloat.includes(page) && user && <FloatingCartButton navigate={navigate} />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: 'Sora, sans-serif', borderRadius: '14px', fontSize: '14px' },
            success: { iconTheme: { primary: '#1DB954', secondary: 'white' } },
          }}
        />
        <AppInner />
      </CartProvider>
    </AuthProvider>
  );
}
